<template>
  <div>
    <GeojsonLayer
      :layer="layerStyleMarker"
      :source="source"
      :sourceId="sourceId"
      @layerClicked="onBuildingMarkerClicked"
      @layerMouseEnter="handleMouseEnterBuilding"
      @layerMouseMove="handleMouseMoveBuilding"
      @layerMouseLeave="handleMouseLeaveBuilding"
    />
    <div v-show="false">
      <MapPopupNew ref="popup" :rows="rows" />
    </div>
  </div>
</template>

<script>
import GeojsonLayer from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import { INDOORVIEWER_PREFIX, MAPVIEW_PREFIX } from '@/store/module-prefixes'
import { CHANGE_BUILDING, RESET_IVION_INSTANCES } from '@/store/action-types'
import { SET_CLICKED_ITEM_TYPE } from '@/store/mutation-types'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { mapState as mapStatePinia } from 'pinia'
import { getRealmId } from '@/utils/realmId'
import mapboxgl from 'mapbox-gl'
import MapPopupNew from '../../popups/MapPopupNew.vue'
import { createSmallSubtitle, createSmallTitle } from '@/components/Builder'
import { useIotCoverageStore } from '@/pinia/iotCoverage'
import { zoomToEntities } from '@/composables/useMapbox'

export default {
  name: 'LayerMarkerBuilding',
  components: {
    GeojsonLayer,
    MapPopupNew
  },
  props: {
    layerIdMarker: {
      type: String,
      required: true
    },
    buildingSource: {
      validator: (prop) => typeof prop === 'object' || prop == null,
      default: null
    },
    complexSource: {
      validator: (prop) => typeof prop === 'object' || prop == null,
      default: null
    }
  },
  emits: ['hover'],
  data() {
    return {
      buildings: null,
      clickedBuildingMarker: '',
      hoveredBuildingId: '',
      popup: new mapboxgl.Popup({ closeButton: false, offset: 60 }),
      popupTitle: '',
      hasSensorData: false,
      subtitle: '',
      hasIndoorView: false,
      sourceId: 'source-marker-building',
      visibilityLimited: ['interpolate', ['linear'], ['zoom'], 6, 0, 7, 1, 14, 1, 15, 0],
      visibilityStandard: ['interpolate', ['linear'], ['zoom'], 14, 1, 15, 0]
    }
  },
  computed: {
    ...mapStatePinia(useIotCoverageStore, {
      constructionsWithIotData: 'constructions'
    }),
    ...mapGetters({
      hasIndoorviewAccess: 'hasIndoorviewAccess',
      hasSensorDataAccess: 'hasSensorDataAccess',
      isMeasurementActive: 'isMeasurementActive'
    }),
    ...mapGetters(MAPVIEW_PREFIX, {
      selectedBuildingName: 'selectedBuildingName'
    }),
    ...mapState({
      clickedItemType: (state) => state.clickedItemType
    }),
    ...mapState(MAPVIEW_PREFIX, {
      selectedBuilding: (state) => state.selectedBuilding
    }),
    isAllBuildingsSelected() {
      return this.selectedBuildingName === 'Alla' && this.clickedItemType === 'building'
    },
    isBuildingMarkerSelected() {
      return this.clickedBuildingMarker !== ''
    },
    isBuildingOrMoreDetailedSelected() {
      return (
        ['room', 'poi', 'line', 'zone', 'level', 'building'].indexOf(this.clickedItemType) !== -1
      )
    },
    isComplexOrMoreDetailedSelected() {
      // Check for all types of (currently) possible markers
      return ['building', 'realEstate', 'complex'].indexOf(this.clickedItemType) !== -1
    },
    isVisibilityThresholdReached() {
      return this.complexSource?.data.features.length > 10
    },
    rows() {
      const icons = []
      const realmId = getRealmId()
      if (this.hasSensorData && this.hasSensorDataAccess) {
        icons.push(require('@/assets/sensor-icon.svg'))
      }
      if (this.hasIndoorView && this.hasIndoorviewAccess) {
        icons.push(require('@/assets/icon-indoorview.svg'))
      }
      return [
        {
          cols: icons.map((src) => ({
            type: 'image',
            style: {
              width: '30px',
              height: '25px',
              paddingRight: '10px'
            },
            props: {
              src
            }
          }))
        },
        createSmallTitle(this.popupTitle),
        createSmallSubtitle(
          realmId === 'akademiskahus'
            ? this.$t('general__data__marking-building') + ': ' + this.subtitle
            : this.subtitle
            ? this.$t('general__data__marking-label') + ': ' + this.subtitle
            : ''
        )
      ]
    },
    layerStyleMarker() {
      return {
        id: this.layerIdMarker,
        source: this.sourceId,
        type: 'symbol',
        maxzoom: 15,
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': [
            'case',
            ['==', this.isBuildingMarkerSelected, false],
            'complex_marker_missing_id_active', // NEW
            ['boolean', ['==', ['get', 'id'], this.clickedBuildingMarker], false],
            'complex_marker_missing_id_active',
            ['boolean', ['==', ['get', 'id'], this.hoveredBuildingId], false],
            'complex_marker_missing_id_active',
            [
              'all',
              this.isComplexOrMoreDetailedSelected,
              ['!=', ['get', 'id'], this.clickedBuildingMarker]
            ],
            'complex_marker_shadow_missing_id_inactive',
            'complex_marker_missing_id_active'
          ],
          'icon-size': 1,
          'icon-allow-overlap': true,
          'icon-anchor': 'bottom'
        },
        paint: {
          'icon-opacity': this.isVisibilityThresholdReached
            ? this.visibilityLimited
            : this.visibilityStandard,
          'text-opacity': this.isVisibilityThresholdReached
            ? this.visibilityLimited
            : this.visibilityStandard
        }
      }
    },
    source() {
      const buildingMarkerSource = {
        type: 'geojson',
        data: {
          id: this.sourceId,
          type: 'FeatureCollection',
          features: []
        },
        cluster: true,
        clusterMaxZoom: 13,
        clusterRadius: 30
      }
      if (this.buildingSource) {
        this.buildingSource.data.features.forEach((building) => {
          if (!building.properties.location || !building.geometry) return
          // Markers are only added if the building has a centre coordinate
          buildingMarkerSource.data.features.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: building.properties.location.coordinates.slice(0, 2)
            },
            id: building.properties.id,
            properties: {
              id: building.properties.id,
              title: building.properties.displayName,
              hasSensorData: this.constructionsWithIotData.includes(building.id),
              marking: building.properties.marking,
              hasIndoorView: building.properties.navvisLinks.length > 0
            }
          })
        })
      }
      return buildingMarkerSource
    }
  },
  watch: {
    selectedBuilding(newBuilding, oldBuilding) {
      if (oldBuilding === null || newBuilding.id !== oldBuilding.id) {
        this.clickedBuildingMarker = newBuilding.id ? newBuilding.id : null
      }
    },
    clickedItemType() {
      if (!this.isComplexOrMoreDetailedSelected) {
        this.clickedBuildingMarker = ''
      }
    }
  },
  methods: {
    ...mapActions({
      dispatchBuilding: CHANGE_BUILDING
    }),
    ...mapActions(INDOORVIEWER_PREFIX, {
      resetBuildingInstances: RESET_IVION_INSTANCES
    }),
    ...mapMutations({
      commitClick: SET_CLICKED_ITEM_TYPE
    }),
    handleMouseEnterBuilding() {
      if (!this.isMeasurementActive) {
        this.setCursor('pointer')
      }
    },
    handleMouseMoveBuilding(event) {
      if (!this.isMeasurementActive) {
        if (!this.isFirstLayer(event)) {
          this.popup.remove()
          this.hoveredBuildingId = null
          this.setCursor('')
        }
        if (this.isFirstLayer(event)) {
          this.setCursor('pointer')
          this.updateHoverBuilding(event)
        }
      }
    },
    handleMouseLeaveBuilding(event) {
      if (!this.isMeasurementActive) {
        this.setCursor('')
        this.updateHoverBuilding(event)
      }
    },
    // Returns true if the first feature (the feature highest on top) in  event
    // comes from the current layer, false otherwise
    isFirstLayer(event) {
      const renderedFeatures = this.$store.map.queryRenderedFeatures(event.point)
      return (
        renderedFeatures[0].source === this.source.data.id &&
        renderedFeatures[0].layer.paint['icon-opacity']
      )
    },
    async onBuildingMarkerClicked(event) {
      if (this.isMeasurementActive || !this.isFirstLayer(event)) return
      const feature = event.features[0]
      this.clickedBuildingMarker = feature.properties.id

      if (this.selectedBuilding.id !== feature.properties.id) {
        this.resetBuildingInstances()
      }
      const building = this.buildingSource.data.features.find(
        (x) => x.properties.id === feature.properties.id
      )

      if (building) {
        this.dispatchBuilding({ building })
        this.commitClick({ type: 'building' })
        zoomToEntities([building.properties.id])
      }
    },
    setCursor(cursorType) {
      this.$store.map.getCanvas().style.cursor = cursorType
    },
    showPopup(event) {
      if (!this.$refs.popup) return
      const coordinates = event.features[0].geometry.coordinates
      this.popup.setLngLat(coordinates).setDOMContent(this.$refs.popup.$el).addTo(this.$store.map)
    },
    updateHoverBuilding(event) {
      let features = event.features
      if (features && features.length > 0) {
        if (this.hoveredBuildingId !== features[0].properties.id) {
          this.popupTitle = features[0].properties.title
          this.hasSensorData = features[0].properties.hasSensorData
          this.subtitle = features[0].properties.marking ? features[0].properties.marking : ''
          this.hasIndoorView = features[0].properties?.hasIndoorView
          this.$emit('hover', features[0].properties.id)
          this.showPopup(event)
        }
        this.hoveredBuildingId = features[0].properties.id
      } else {
        this.$emit('hover', '')
        this.hoveredBuildingId = null
        this.popup.remove()
        this.setCursor('')
      }
    }
  }
}
</script>
